import React, { useRef } from 'react';
import * as Styled from './OCardBrowse.styles';
import { OCardBrowseProps } from './OCardBrowse.types';
import { MMediaImage } from '../../molecules/MMediaImage';
import { MTextBlockBasic } from '../../molecules/MTextBlockBasic';
import { MTextBlockBasicAlignment } from '../../molecules/MTextBlockBasic/MTextBlockBasic.types';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { AIcon } from '../../atoms/AIcon';
import { AIconSize } from '../../atoms/AIcon/AIcon.types';
import { useTracking } from '../../hooks';
import {
  AemAspectRatio,
  AemToFigmaAspectRatio,
  AspectRatio,
  MediaQuery,
  ResponsiveImageSpec,
  ViewportPx,
} from '../../utils/ImageUtils';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OCardBrowse';
const TEST_ID = 'web-ui-kit-OCardBrowse';
const LINK_TEST_ID = 'web-ui-kit-OCardBrowse-link';

// AEM aspect ratios OCardBrowse supports. Anything else (or missing)
// falls back to 3:2 (CLASSIC_HOR).
const SUPPORTED_ASPECT_RATIOS: ReadonlySet<AemAspectRatio> = new Set([
  AemAspectRatio.STANDARD_HOR,
  AemAspectRatio.WIDE_HOR,
  AemAspectRatio.CLASSIC_HOR,
  AemAspectRatio.SQUARE,
  AemAspectRatio.CLASSIC_VER,
]);

// Full-width card: the image is requested at ~= the full viewport width at each
// breakpoint's upper edge, i.e. the min-width of the next larger breakpoint.
const RENDITION_WIDTHS: { media: MediaQuery; imgWidthPx: number }[] = [
  { media: MediaQuery.MIN_WIDTH_XS, imgWidthPx: ViewportPx.SM },
  { media: MediaQuery.MIN_WIDTH_SM, imgWidthPx: ViewportPx.MD },
  { media: MediaQuery.MIN_WIDTH_MD, imgWidthPx: ViewportPx.LG },
  { media: MediaQuery.MIN_WIDTH_LG, imgWidthPx: ViewportPx.XL },
  { media: MediaQuery.MIN_WIDTH_XL, imgWidthPx: ViewportPx.XXL },
];

const buildRenditionSpecs = (aspectRatioInput?: AemAspectRatio | `${AemAspectRatio}`): ResponsiveImageSpec[] => {
  const key = aspectRatioInput as AemAspectRatio | undefined;
  const aspectRatio = key && SUPPORTED_ASPECT_RATIOS.has(key) ? AemToFigmaAspectRatio[key] : AspectRatio.CLASSIC_HOR;
  return RENDITION_WIDTHS.map(({ media, imgWidthPx }) => ({ media, aspectRatio, imgWidthPx }));
};

const OCardBrowse = React.forwardRef<HTMLDivElement, OCardBrowseProps>((props, ref) => {
  const {
    ctaLink,
    mMediaImage,
    mTextBlockBasic,
    isAuthorMode,
    trackingProperties,
    custom_click_track_value,
    preTrackVal,
    isExternal,
    className,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const subheaderText = mTextBlockBasic?.aSubheader?.text;
  const hasRequiredContent = !!ctaLink && !!subheaderText;

  const internalRef = useRef<HTMLDivElement>(null);
  const elementRef = (ref ?? internalRef) as React.RefObject<HTMLDivElement>;
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useTracking({
    ref: anchorRef as React.RefObject<HTMLElement>,
    trackingProperties,
    isExternal,
    custom_click_track_value,
    preTrackVal,
    text: subheaderText,
  });

  if (!hasRequiredContent && !isAuthorMode) return null;

  const textBlock = mTextBlockBasic
    ? {
        ...mTextBlockBasic,
        alignment: MTextBlockBasicAlignment.Left,
        aSubheader: mTextBlockBasic.aSubheader
          ? {
              ...mTextBlockBasic.aSubheader,
              size: mTextBlockBasic.aSubheader.size ?? ASubheaderSize.L,
            }
          : undefined,
        aBody: mTextBlockBasic.aBody
          ? {
              ...mTextBlockBasic.aBody,
              size: mTextBlockBasic.aBody.size ?? ABodySize.S,
              color: mTextBlockBasic.aBody.color ?? ABodyColor.Alternate,
            }
          : undefined,
      }
    : undefined;

  return (
    <Styled.Wrapper
      ref={elementRef}
      className={className}
      {...rest}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
    >
      {mMediaImage && (
        <MMediaImage renditionSpecs={buildRenditionSpecs(mMediaImage.aImage?.aspectRatio)} {...mMediaImage} />
      )}
      <Styled.ContentRow
        ref={anchorRef}
        data-testid={LINK_TEST_ID}
        aria-label={ariaLabel || subheaderText || undefined}
        {...(ctaLink ? { href: ctaLink } : {})}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        <Styled.TextBlockWrapper>{textBlock && <MTextBlockBasic {...textBlock} />}</Styled.TextBlockWrapper>
        <Styled.IconWrapper>
          <AIcon icon="icon-arrow-right" size={AIconSize.L} aria-hidden="true" />
        </Styled.IconWrapper>
      </Styled.ContentRow>
    </Styled.Wrapper>
  );
});

OCardBrowse.displayName = 'OCardBrowse';

export { OCardBrowse, TEST_ID as O_CARD_BROWSE_TEST_ID, LINK_TEST_ID as O_CARD_BROWSE_LINK_TEST_ID };
